import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/kfmApi";
import { buildCompiledText, printCompiledDocument } from "@/lib/studyText";
import { Loader2, ArrowLeft, Printer, Layers, ChevronRight, MessageCircle, NotebookPen, Copy, Check } from "lucide-react";
import { toast } from "sonner";

// Phase C — every visit to one passage (book+chapter) in one place, with a combined export.
export default function PassageHistory() {
  const { key } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/study/passages/${encodeURIComponent(key)}`);
        setData(data);
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || "Couldn't load this passage's history");
      }
    })();
  }, [key]);

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(buildCompiledText(data.label, data.sessions));
      setCopied(true);
      toast.success("All visits copied — paste it anywhere");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const printAll = () => {
    const ok = printCompiledDocument(data.label, data.sessions);
    if (!ok) toast.error("Please allow pop-ups to print or save as PDF.");
  };

  const answerCount = (s) => Object.keys(s.answers || {}).filter((k) => String(s.answers[k] || "").trim()).length;
  const chatCount = (s) => (s.chat || []).filter((m) => (m.content || "").trim()).length;

  if (error) {
    return (
      <div className="px-6 py-16 text-center space-y-4 md:max-w-2xl md:mx-auto">
        <p className="text-slate-400">{error}</p>
        <button onClick={() => navigate("/history")} className="text-[#00E5FF] hover:underline inline-flex items-center gap-1.5">
          <ArrowLeft className="w-4 h-4" /> Back to your journey
        </button>
      </div>
    );
  }

  if (!data) {
    return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 text-[#00E5FF] animate-spin" strokeWidth={1.5} /></div>;
  }

  return (
    <div className="px-6 py-10 space-y-8 md:max-w-2xl md:mx-auto md:py-14 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <button
        data-testid="passage-back"
        onClick={() => navigate("/history")}
        className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#00E5FF] transition-colors"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Your journey
      </button>

      <header className="space-y-2">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-[#00E5FF]">
          <Layers className="w-4 h-4" strokeWidth={1.5} /> Passage history
        </div>
        <h1 data-testid="passage-title" className="text-3xl text-white">{data.label}</h1>
        <p className="text-sm text-slate-400">
          Everything you've drawn from this passage across <strong>{data.count}</strong> {data.count === 1 ? "visit" : "visits"} — gathered here to review or build into a finished study.
        </p>
      </header>

      {/* Combined export */}
      <div className="flex flex-wrap gap-2">
        <button
          data-testid="passage-copy-all"
          onClick={copyAll}
          className="inline-flex items-center gap-2 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] pl-3 pr-4 py-2 text-sm font-medium transition-colors active:scale-95"
        >
          {copied ? <Check className="w-4 h-4" strokeWidth={1.75} /> : <Copy className="w-4 h-4" strokeWidth={1.75} />}
          {copied ? "Copied" : "Copy all visits"}
        </button>
        <button
          data-testid="passage-print-all"
          onClick={printAll}
          className="inline-flex items-center gap-2 rounded-full bg-[#0E131F] hover:bg-[#10222A] text-[#00B8CC] border border-[#00E5FF]/20 pl-3 pr-4 py-2 text-sm font-medium transition-colors active:scale-95"
        >
          <Printer className="w-4 h-4" strokeWidth={1.75} /> Export all (PDF)
        </button>
      </div>

      {/* Chronological visits */}
      <div className="space-y-3" data-testid="passage-visits">
        {data.sessions.map((s, i) => (
          <button
            key={s.id}
            data-testid={`passage-visit-${s.id}`}
            onClick={() => navigate(`/study/${s.id}`)}
            className="w-full text-left bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5 flex items-center justify-between gap-3 hover:border-[#00E5FF]/30 transition-colors"
          >
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-slate-500">
                Visit {i + 1}{s.created_at ? ` · ${new Date(s.created_at).toLocaleDateString()}` : ""}
              </div>
              <div className="text-lg text-slate-200 truncate mt-0.5">{s.study?.title || s.reference}</div>
              <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                <span className="text-[#00E5FF]">{s.reference}</span>
                {chatCount(s) > 0 && (
                  <span className="inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" strokeWidth={1.75} /> {chatCount(s)} Go Deeper</span>
                )}
                {answerCount(s) > 0 && (
                  <span className="inline-flex items-center gap-1"><NotebookPen className="w-3 h-3" strokeWidth={1.75} /> {answerCount(s)} answered</span>
                )}
                {s.notes && s.notes.trim() && (
                  <span className="inline-flex items-center gap-1"><NotebookPen className="w-3 h-3" strokeWidth={1.75} /> notes</span>
                )}
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-500 shrink-0" strokeWidth={1.5} />
          </button>
        ))}
      </div>
    </div>
  );
}
